import dubaiImage from "@/assets/dubai-penthouse.jpg";
import spainImage from "@/assets/spain-villa.jpg";
import monacoImage from "@/assets/monaco-view.jpg";
import amalfiImage from "@/assets/amalfi-villa.jpg";
import sardiniaImage from "@/assets/sardinia-estate.jpg";
import dubaiMarinaImage from "@/assets/dubai-marina.jpg";
import palmJumeirahImage from "@/assets/palm-jumeirah.jpg";
import tuscanyImage from "@/assets/tuscany-villa.jpg";
import comoImage from "@/assets/como-villa.jpg";
import ibizaImage from "@/assets/ibiza-villa.jpg";
import barcelonaImage from "@/assets/barcelona-penthouse.jpg";
import mallorcaImage from "@/assets/mallorca-villa.jpg";
import monacoPenthouseImage from "@/assets/monaco-penthouse.jpg";
import monacoWaterfrontImage from "@/assets/monaco-waterfront.jpg";
import dubaiDowntownImage from "@/assets/dubai-downtown.jpg";
import emiratesHillsImage from "@/assets/emirates-hills.jpg";
// New unique images for properties that were using duplicates
import monacoCarreDorImage from "@/assets/monaco-carre-dor.jpg";
import monacoLarvottoImage from "@/assets/monaco-larvotto.jpg";
import sicilyTaorminaImage from "@/assets/sicily-taormina.jpg";
import marbellaGoldenMileImage from "@/assets/marbella-golden-mile.jpg";
import bluewatersImage from "@/assets/bluewaters-dubai.jpg";

export interface Property {
  id: string;
  title: string;
  location: string;
  price: string;
  image: string;
  beds: number;
  baths: number;
  sqm: string; // Square metres for European market
  featured: boolean;
  description: string;
  features: string[];
  gallery: string[];
  virtualTourUrl: string;
  floorPlan: {
    levels: { name: string; rooms: string[] }[];
  };
}

export const properties: Property[] = [
  // ITALY - 5 properties
  {
    id: "villa-positano",
    title: "Villa Positano",
    location: "Amalfi Coast, Italy",
    price: "€9,800,000",
    image: amalfiImage,
    beds: 6,
    baths: 7,
    sqm: "855",
    featured: true,
    description: "Perched on the dramatic cliffs of the Amalfi Coast, Villa Positano offers an extraordinary blend of historic Italian charm and contemporary luxury. This meticulously restored 18th-century estate features hand-painted majolica tiles, vaulted ceilings, and panoramic terraces cascading down to the azure Mediterranean.",
    features: [
      "Panoramic sea views",
      "Historic architecture",
      "Private lemon grove",
      "Infinity pool overlooking the coast",
      "Wine cellar with tasting room",
      "Helipad access nearby",
      "Private beach access",
      "Outdoor dining terrace"
    ],
    gallery: [amalfiImage, amalfiImage, amalfiImage, amalfiImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Villa", rooms: ["Grand Salon", "Formal Dining", "Italian Kitchen", "Master Suite", "Library"] },
        { name: "Upper Terrace", rooms: ["3 Guest Suites", "Reading Room", "Panoramic Balcony"] },
        { name: "Garden Level", rooms: ["Pool House", "Summer Kitchen", "Wine Cellar", "Staff Quarters"] }
      ]
    }
  },
  {
    id: "costa-smeralda-estate",
    title: "Costa Smeralda Estate",
    location: "Sardinia, Italy",
    price: "€15,500,000",
    image: sardiniaImage,
    beds: 8,
    baths: 9,
    sqm: "1,300",
    featured: true,
    description: "Set within the exclusive enclave of Porto Cervo, this magnificent estate represents the pinnacle of Mediterranean luxury living. The property spans over two hectares of manicured gardens with direct access to Sardinia's most pristine turquoise waters.",
    features: [
      "Private beach & dock",
      "Infinity pool with sea views",
      "Professional tennis court",
      "Outdoor entertaining pavilion",
      "Guest cottage",
      "Landscaped Mediterranean gardens",
      "Smart home technology",
      "Secure gated entrance"
    ],
    gallery: [sardiniaImage, sardiniaImage, sardiniaImage, sardiniaImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Residence", rooms: ["Living Pavilion", "Dining Hall", "Chef's Kitchen", "Media Room", "Office"] },
        { name: "Private Wing", rooms: ["Master Suite", "4 Guest Suites", "Wellness Spa"] },
        { name: "Guest House", rooms: ["3 Bedroom Suite", "Kitchenette", "Private Garden"] },
        { name: "Beach Level", rooms: ["Pool House", "Beach Bar", "Equipment Storage"] }
      ]
    }
  },
  {
    id: "tuscany-estate",
    title: "Tuscan Wine Estate",
    location: "Tuscany, Italy",
    price: "€11,200,000",
    image: tuscanyImage,
    beds: 9,
    baths: 10,
    sqm: "1,533",
    featured: false,
    description: "A magnificent Tuscan estate set among rolling hills and vineyards in the heart of Chianti. This historic property combines centuries of Italian heritage with modern luxury, featuring its own producing vineyard, olive groves, and breathtaking views of the Tuscan countryside.",
    features: [
      "Private vineyard & winery",
      "Olive grove production",
      "Heated infinity pool",
      "Historic frescoed ceilings",
      "Professional kitchen",
      "Wine caves",
      "Guest cottages",
      "Helicopter landing"
    ],
    gallery: [tuscanyImage, tuscanyImage, tuscanyImage, tuscanyImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Villa", rooms: ["Grand Salon", "Formal Dining", "Tuscan Kitchen", "Library", "Office"] },
        { name: "Private Floor", rooms: ["Master Suite", "5 Guest Suites", "Family Room"] },
        { name: "Lower Level", rooms: ["Wine Cellar", "Tasting Room", "Gym", "Cinema"] },
        { name: "Guest Houses", rooms: ["3 Independent Cottages", "Staff Quarters"] }
      ]
    }
  },
  {
    id: "lake-como-villa",
    title: "Villa Bellagio",
    location: "Lake Como, Italy",
    price: "€22,000,000",
    image: comoImage,
    beds: 7,
    baths: 8,
    sqm: "1,189",
    featured: true,
    description: "An exceptional lakefront villa on the shores of Lake Como, offering unparalleled views of the Italian Alps. This historic property has been meticulously restored to showcase original frescoes, marble floors, and botanical gardens that descend to a private boat dock.",
    features: [
      "Private boat dock",
      "Historic botanical gardens",
      "Original frescoed ceilings",
      "Heated pool with lake views",
      "Guest house",
      "Direct lake access",
      "Climate-controlled art storage",
      "Caretaker's residence"
    ],
    gallery: [comoImage, comoImage, comoImage, comoImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Ground Floor", rooms: ["Grand Entrance", "Formal Reception", "Dining Salon", "Italian Kitchen"] },
        { name: "Piano Nobile", rooms: ["Master Suite", "4 Guest Suites", "Private Study"] },
        { name: "Garden Level", rooms: ["Pool House", "Wine Cellar", "Staff Quarters"] },
        { name: "Lakefront", rooms: ["Boat House", "Guest Cottage", "Entertaining Terrace"] }
      ]
    }
  },
  {
    id: "sicily-coastal-estate",
    title: "Taormina Cliff Estate",
    location: "Sicily, Italy",
    price: "€7,500,000",
    image: sicilyTaorminaImage,
    beds: 5,
    baths: 6,
    sqm: "725",
    featured: false,
    description: "A stunning cliffside villa in the ancient town of Taormina, offering breathtaking views of Mount Etna and the Ionian Sea. This architectural gem combines traditional Sicilian craftsmanship with contemporary design.",
    features: [
      "Mount Etna & sea views",
      "Infinity pool",
      "Historic architecture",
      "Citrus grove",
      "Outdoor kitchen",
      "Private path to beach",
      "Wine cellar",
      "Staff quarters"
    ],
    gallery: [sicilyTaorminaImage, sicilyTaorminaImage, sicilyTaorminaImage, sicilyTaorminaImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Level", rooms: ["Living Room", "Dining Room", "Kitchen", "Study"] },
        { name: "Upper Level", rooms: ["Master Suite", "3 Guest Bedrooms", "Family Lounge"] },
        { name: "Lower Level", rooms: ["Wine Cellar", "Gym", "Staff Quarters"] }
      ]
    }
  },

  // SPAIN - 5 properties
  {
    id: "villa-serenidad",
    title: "Villa Serenidad",
    location: "Marbella, Spain",
    price: "€8,200,000",
    image: spainImage,
    beds: 7,
    baths: 8,
    sqm: "1,115",
    featured: true,
    description: "Nestled in the prestigious La Zagaleta estate, Villa Serenidad embodies Mediterranean elegance at its finest. This architectural masterpiece seamlessly blends indoor and outdoor living with expansive terraces, manicured gardens, and breathtaking views of the Mediterranean Sea and Atlas Mountains.",
    features: [
      "Heated infinity pool",
      "Private spa & hammam",
      "Tennis court",
      "Guest house",
      "Mediterranean gardens",
      "State-of-art security",
      "Underground garage",
      "Golf course views"
    ],
    gallery: [spainImage, spainImage, spainImage, spainImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Ground Floor", rooms: ["Grand Foyer", "Living Salon", "Formal Dining", "Chef's Kitchen", "Library"] },
        { name: "First Floor", rooms: ["Master Suite", "4 Guest Suites", "Family Lounge", "Dressing Rooms"] },
        { name: "Lower Level", rooms: ["Cinema Room", "Gym", "Wine Cellar", "Staff Quarters"] },
        { name: "Guest House", rooms: ["2 Bedroom Suite", "Kitchen", "Private Terrace"] }
      ]
    }
  },
  {
    id: "ibiza-sunset-villa",
    title: "Ibiza Sunset Villa",
    location: "Ibiza, Spain",
    price: "€12,800,000",
    image: ibizaImage,
    beds: 6,
    baths: 7,
    sqm: "883",
    featured: true,
    description: "An iconic contemporary villa in Ibiza's most exclusive enclave, offering spectacular sunset views over the Mediterranean. This architectural masterpiece features minimalist design with floor-to-ceiling windows, infinity pool, and direct access to a private cove.",
    features: [
      "Legendary sunset views",
      "Infinity edge pool",
      "Private cove access",
      "DJ booth & sound system",
      "Outdoor cinema",
      "Professional kitchen",
      "Guest cottage",
      "Helipad"
    ],
    gallery: [ibizaImage, ibizaImage, ibizaImage, ibizaImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Level", rooms: ["Open Living Space", "Designer Kitchen", "Master Suite", "Entertainment Area"] },
        { name: "Upper Level", rooms: ["3 Guest Suites", "Yoga Studio", "Rooftop Terrace"] },
        { name: "Lower Level", rooms: ["Spa & Wellness", "Gym", "Wine Room"] },
        { name: "Guest House", rooms: ["2 Bedroom Suite", "Kitchen", "Private Pool"] }
      ]
    }
  },
  {
    id: "barcelona-sky-penthouse",
    title: "Barcelona Sky Penthouse",
    location: "Barcelona, Spain",
    price: "€9,500,000",
    image: barcelonaImage,
    beds: 4,
    baths: 5,
    sqm: "632",
    featured: false,
    description: "An extraordinary duplex penthouse in Barcelona's most prestigious neighborhood, offering panoramic views from the Sagrada Familia to the Mediterranean Sea. This ultra-modern residence features a private rooftop pool, extensive terraces, and world-class finishes.",
    features: [
      "360-degree city views",
      "Private rooftop pool",
      "Smart home system",
      "Wine cellar",
      "Private elevator",
      "Concierge services",
      "Underground parking",
      "Guest suite"
    ],
    gallery: [barcelonaImage, barcelonaImage, barcelonaImage, barcelonaImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Lower Level", rooms: ["Living Room", "Dining Area", "Gourmet Kitchen", "Guest Suite", "Office"] },
        { name: "Upper Level", rooms: ["Master Suite", "2 Guest Bedrooms", "Family Room"] },
        { name: "Rooftop", rooms: ["Pool Deck", "Outdoor Kitchen", "Lounge Area", "Garden"] }
      ]
    }
  },
  {
    id: "mallorca-cliffside",
    title: "Mallorca Cliffside Estate",
    location: "Mallorca, Spain",
    price: "€14,200,000",
    image: mallorcaImage,
    beds: 8,
    baths: 9,
    sqm: "1,254",
    featured: false,
    description: "A breathtaking cliffside estate on Mallorca's dramatic southwest coast, offering uninterrupted views of the Mediterranean and the island of Cabrera. This exceptional property combines traditional Mallorcan architecture with contemporary luxury.",
    features: [
      "Dramatic cliff location",
      "Infinity pool with sea views",
      "Private beach access",
      "Olive & citrus groves",
      "Tennis court",
      "Wine cellar",
      "Guest house",
      "Boat mooring"
    ],
    gallery: [mallorcaImage, mallorcaImage, mallorcaImage, mallorcaImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main House", rooms: ["Grand Living Room", "Formal Dining", "Chef's Kitchen", "Library"] },
        { name: "Bedroom Wing", rooms: ["Master Suite", "4 Guest Suites", "Dressing Rooms"] },
        { name: "Lower Level", rooms: ["Spa", "Gym", "Cinema", "Wine Cellar"] },
        { name: "Guest House", rooms: ["3 Bedroom Suite", "Kitchen", "Private Terrace"] }
      ]
    }
  },
  {
    id: "marbella-golden-mile",
    title: "Golden Mile Mansion",
    location: "Marbella, Spain",
    price: "€18,500,000",
    image: marbellaGoldenMileImage,
    beds: 9,
    baths: 10,
    sqm: "1,672",
    featured: false,
    description: "An exceptional beachfront mansion on Marbella's legendary Golden Mile, offering direct Mediterranean beach access and unparalleled luxury. This palatial residence features classical architecture, extensive gardens, and resort-style amenities.",
    features: [
      "Beachfront location",
      "Private beach access",
      "Olympic-size pool",
      "Tennis court",
      "Spa & wellness center",
      "Guest house",
      "Staff quarters",
      "24/7 security"
    ],
    gallery: [marbellaGoldenMileImage, marbellaGoldenMileImage, marbellaGoldenMileImage, marbellaGoldenMileImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Ground Floor", rooms: ["Reception Hall", "Living Salon", "Dining Room", "Kitchen", "Office"] },
        { name: "First Floor", rooms: ["Master Suite", "5 Guest Suites", "Family Room"] },
        { name: "Lower Level", rooms: ["Cinema", "Spa", "Gym", "Wine Cellar", "Game Room"] },
        { name: "Guest House", rooms: ["3 Bedrooms", "Kitchen", "Private Garden"] }
      ]
    }
  },

  // MONACO - 5 properties
  {
    id: "monte-carlo-heights",
    title: "Monte Carlo Heights",
    location: "Monaco",
    price: "€28,000,000",
    image: monacoImage,
    beds: 4,
    baths: 5,
    sqm: "576",
    featured: true,
    description: "An unparalleled opportunity to own one of Monaco's most coveted addresses. This exceptional apartment offers sweeping views of the legendary Monte Carlo harbor and Mediterranean beyond. Designed by world-renowned architects and finished to the highest specifications.",
    features: [
      "Harbor & sea views",
      "Private terrace",
      "Hermès interior design",
      "Climate-controlled art storage",
      "Professional-grade kitchen",
      "Building concierge",
      "Secure parking spaces",
      "Direct beach access"
    ],
    gallery: [monacoImage, monacoImage, monacoImage, monacoImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Level", rooms: ["Double Reception", "Dining Area", "Bespoke Kitchen", "Master Suite", "Office"] },
        { name: "Upper Level", rooms: ["3 Guest Suites", "Private Lounge", "Terrace Access"] }
      ]
    }
  },
  {
    id: "monaco-harbor-penthouse",
    title: "Port Hercules Penthouse",
    location: "Monaco",
    price: "€45,000,000",
    image: monacoPenthouseImage,
    beds: 5,
    baths: 6,
    sqm: "790",
    featured: true,
    description: "The ultimate Monaco residence overlooking the legendary Port Hercules and the Grand Prix circuit. This exceptional triplex penthouse offers 360-degree views, a private rooftop pool, and direct access to Monaco's most exclusive amenities.",
    features: [
      "Grand Prix circuit views",
      "Private rooftop pool",
      "Wine cellar",
      "Smart home technology",
      "Private elevator",
      "Yacht club access",
      "24/7 concierge",
      "Secure parking for 4 cars"
    ],
    gallery: [monacoPenthouseImage, monacoPenthouseImage, monacoPenthouseImage, monacoPenthouseImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Reception Level", rooms: ["Grand Salon", "Formal Dining", "Chef's Kitchen", "Office"] },
        { name: "Private Level", rooms: ["Master Suite", "3 Guest Suites", "Family Room"] },
        { name: "Rooftop", rooms: ["Pool Terrace", "Outdoor Kitchen", "Lounge", "Spa"] }
      ]
    }
  },
  {
    id: "monaco-waterfront-residence",
    title: "Fontvieille Marina Residence",
    location: "Monaco",
    price: "€19,500,000",
    image: monacoWaterfrontImage,
    beds: 3,
    baths: 4,
    sqm: "446",
    featured: false,
    description: "An elegant waterfront apartment in Monaco's prestigious Fontvieille district, offering spectacular marina views and yacht-side living. This meticulously designed residence combines classic Mediterranean style with modern luxury.",
    features: [
      "Marina views",
      "Large terrace",
      "High-end finishes",
      "Building pool & gym",
      "Concierge services",
      "Secure parking",
      "Storage unit",
      "Walking distance to amenities"
    ],
    gallery: [monacoWaterfrontImage, monacoWaterfrontImage, monacoWaterfrontImage, monacoWaterfrontImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Level", rooms: ["Living Room", "Dining Area", "Kitchen", "Master Suite", "2 Guest Bedrooms"] }
      ]
    }
  },
  {
    id: "monaco-carre-dor",
    title: "Carré d'Or Apartment",
    location: "Monaco",
    price: "€32,000,000",
    image: monacoCarreDorImage,
    beds: 4,
    baths: 5,
    sqm: "511",
    featured: false,
    description: "A prestigious apartment in Monaco's Golden Square, the principality's most exclusive neighborhood. Steps from the Casino de Monte-Carlo and the Hotel de Paris, this residence offers unmatched luxury and location.",
    features: [
      "Prime Carré d'Or location",
      "Casino & sea views",
      "Bespoke interiors",
      "Smart home system",
      "Wine cellar",
      "24/7 security",
      "Valet parking",
      "Concierge services"
    ],
    gallery: [monacoCarreDorImage, monacoCarreDorImage, monacoCarreDorImage, monacoCarreDorImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Level", rooms: ["Reception Room", "Dining Room", "Kitchen", "Master Suite"] },
        { name: "Upper Level", rooms: ["3 Guest Suites", "Office", "Terrace"] }
      ]
    }
  },
  {
    id: "monaco-larvotto",
    title: "Larvotto Beach Residence",
    location: "Monaco",
    price: "€24,000,000",
    image: monacoLarvottoImage,
    beds: 4,
    baths: 4,
    sqm: "483",
    featured: false,
    description: "An exceptional beachfront apartment overlooking Monaco's Larvotto Beach. This rare property offers direct beach access, stunning Mediterranean views, and proximity to Monaco's finest restaurants and boutiques.",
    features: [
      "Beachfront location",
      "Mediterranean views",
      "Large terraces",
      "Modern design",
      "Building amenities",
      "Private beach access",
      "Secure parking",
      "Walking distance to shopping"
    ],
    gallery: [monacoLarvottoImage, monacoLarvottoImage, monacoLarvottoImage, monacoLarvottoImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Level", rooms: ["Open Living Area", "Kitchen", "Master Suite", "3 Guest Bedrooms", "Terrace"] }
      ]
    }
  },

  // DUBAI - 6 properties
  {
    id: "burj-khalifa-residence",
    title: "Burj Khalifa Residence",
    location: "Dubai, UAE",
    price: "€12,500,000",
    image: dubaiImage,
    beds: 5,
    baths: 6,
    sqm: "790",
    featured: true,
    description: "Experience the pinnacle of luxury living in this extraordinary penthouse residence within the iconic Burj Khalifa. Spanning an entire floor with 360-degree panoramic views of the Arabian Gulf, Palm Jumeirah, and the glittering Dubai skyline.",
    features: [
      "Private elevator access",
      "Infinity pool with city views",
      "Smart home automation",
      "Wine cellar & tasting room",
      "Private cinema room",
      "Concierge service 24/7",
      "Secure parking for 4 vehicles",
      "Rooftop terrace"
    ],
    gallery: [dubaiImage, dubaiImage, dubaiImage, dubaiImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Level", rooms: ["Grand Living Room", "Formal Dining", "Gourmet Kitchen", "Guest Suite", "Office"] },
        { name: "Upper Level", rooms: ["Master Suite", "3 Guest Bedrooms", "Family Room", "Spa & Wellness Center"] },
        { name: "Rooftop", rooms: ["Private Pool", "Entertainment Pavilion", "Outdoor Kitchen", "Garden Terrace"] }
      ]
    }
  },
  {
    id: "dubai-marina-penthouse",
    title: "Marina Skyline Penthouse",
    location: "Dubai Marina, UAE",
    price: "€8,900,000",
    image: dubaiMarinaImage,
    beds: 4,
    baths: 5,
    sqm: "669",
    featured: false,
    description: "Rising above the iconic Dubai Marina, this ultra-modern penthouse offers an unrivaled lifestyle in one of the world's most dynamic cities. Floor-to-ceiling windows frame spectacular views of the marina, Palm Jumeirah, and the Arabian Gulf.",
    features: [
      "360-degree panoramic views",
      "Private rooftop terrace",
      "Smart home automation",
      "Chef's kitchen with Gaggenau appliances",
      "Private elevator",
      "Temperature-controlled wine room",
      "Home cinema",
      "3 premium parking spaces"
    ],
    gallery: [dubaiMarinaImage, dubaiMarinaImage, dubaiMarinaImage, dubaiMarinaImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Level", rooms: ["Open Living Area", "Dining Room", "Gourmet Kitchen", "Guest Suite"] },
        { name: "Upper Level", rooms: ["Master Suite", "2 Guest Bedrooms", "Family Room", "Home Office"] },
        { name: "Rooftop", rooms: ["Sky Lounge", "Outdoor Kitchen", "Jacuzzi", "Entertainment Deck"] }
      ]
    }
  },
  {
    id: "palm-jumeirah-villa",
    title: "Palm Jumeirah Signature Villa",
    location: "Palm Jumeirah, Dubai",
    price: "€18,500,000",
    image: palmJumeirahImage,
    beds: 6,
    baths: 7,
    sqm: "1,068",
    featured: true,
    description: "An exceptional waterfront villa on the prestigious Palm Jumeirah frond, offering private beach access and unobstructed views of the Arabian Gulf. This contemporary masterpiece features soaring double-height ceilings and world-class finishes.",
    features: [
      "Private beach & infinity pool",
      "Direct sea access",
      "Luxury Italian finishes",
      "Smart home integration",
      "Staff quarters",
      "Private dock for yacht",
      "Landscaped tropical gardens",
      "24/7 security & concierge"
    ],
    gallery: [palmJumeirahImage, palmJumeirahImage, palmJumeirahImage, palmJumeirahImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Ground Floor", rooms: ["Grand Reception", "Formal Living", "Dining Room", "Kitchen", "Guest Suite"] },
        { name: "First Floor", rooms: ["Master Suite", "4 Guest Bedrooms", "Family Lounge", "Study"] },
        { name: "Basement", rooms: ["Cinema Room", "Gym", "Spa", "Staff Quarters", "Wine Cellar"] },
        { name: "Exterior", rooms: ["Beach Cabana", "Pool Deck", "Outdoor Kitchen", "Private Dock"] }
      ]
    }
  },
  {
    id: "downtown-dubai-penthouse",
    title: "Downtown Boulevard Penthouse",
    location: "Downtown Dubai, UAE",
    price: "€15,200,000",
    image: dubaiDowntownImage,
    beds: 5,
    baths: 6,
    sqm: "910",
    featured: false,
    description: "An extraordinary penthouse in the heart of Downtown Dubai, offering front-row views of the Burj Khalifa and Dubai Fountain. This sophisticated residence combines contemporary design with traditional Arabian elements.",
    features: [
      "Burj Khalifa views",
      "Dubai Fountain views",
      "Private terrace",
      "Smart home system",
      "Private elevator",
      "Wine cellar",
      "Cinema room",
      "5 parking spaces"
    ],
    gallery: [dubaiDowntownImage, dubaiDowntownImage, dubaiDowntownImage, dubaiDowntownImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Level", rooms: ["Grand Living Room", "Formal Dining", "Kitchen", "Office", "Guest Suite"] },
        { name: "Upper Level", rooms: ["Master Suite", "3 Guest Bedrooms", "Family Room", "Terrace"] }
      ]
    }
  },
  {
    id: "emirates-hills-mansion",
    title: "Emirates Hills Estate",
    location: "Emirates Hills, Dubai",
    price: "€25,000,000",
    image: emiratesHillsImage,
    beds: 8,
    baths: 10,
    sqm: "2,044",
    featured: true,
    description: "A palatial mansion in Dubai's most prestigious gated community, Emirates Hills. This exceptional property features Mediterranean-inspired architecture, championship golf course views, and the finest luxury amenities available.",
    features: [
      "Golf course views",
      "Olympic pool",
      "Tennis court",
      "Private cinema",
      "Spa & wellness center",
      "Staff quarters",
      "10-car garage",
      "Landscaped gardens"
    ],
    gallery: [emiratesHillsImage, emiratesHillsImage, emiratesHillsImage, emiratesHillsImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Ground Floor", rooms: ["Grand Foyer", "Reception Rooms", "Formal Dining", "Kitchen", "Office"] },
        { name: "First Floor", rooms: ["Master Suite", "5 Guest Suites", "Family Room", "Library"] },
        { name: "Lower Level", rooms: ["Cinema", "Spa", "Gym", "Game Room", "Wine Cellar", "Staff Quarters"] },
        { name: "Exterior", rooms: ["Pool House", "Tennis Court", "Gardens", "Garage"] }
      ]
    }
  },
  {
    id: "bluewaters-island-penthouse",
    title: "Bluewaters Island Penthouse",
    location: "Bluewaters Island, Dubai",
    price: "€11,800,000",
    image: bluewatersImage,
    beds: 4,
    baths: 5,
    sqm: "604",
    featured: false,
    description: "A stunning penthouse on Dubai's Bluewaters Island, home to Ain Dubai, the world's largest observation wheel. This contemporary residence offers spectacular views and access to world-class dining and entertainment.",
    features: [
      "Ain Dubai views",
      "Beach access",
      "Rooftop terrace",
      "Smart home system",
      "Private elevator",
      "Building amenities",
      "Secure parking",
      "Concierge services"
    ],
    gallery: [bluewatersImage, bluewatersImage, bluewatersImage, bluewatersImage],
    virtualTourUrl: "https://my.matterport.com/show/?m=SxQL3iGyvQk",
    floorPlan: {
      levels: [
        { name: "Main Level", rooms: ["Open Living", "Dining Area", "Kitchen", "Master Suite", "Guest Suite"] },
        { name: "Upper Level", rooms: ["2 Guest Bedrooms", "Family Room", "Rooftop Access"] }
      ]
    }
  }
];

export const getPropertyById = (id: string): Property | undefined => {
  return properties.find(property => property.id === id);
};
